import { ChevronRight } from "lucide-react";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Link, useMatches } from "react-router-dom";

interface CrumbHandle {
  /**
   * Chave i18n (preferida). Ex: ``"nav.crumbs.teams"`` -- resolve via
   * ``t(key)`` no namespace ``common``.
   */
  crumbKey?: string;
  /**
   * Label literal (fallback / legado). Pode ser uma string fixa ou uma
   * funcao que recebe ``match.data`` (ex.: titulo dinamico de uma
   * conversa). Quando ``crumbKey`` esta presente, este campo eh
   * ignorado.
   */
  crumb?: string | ((data: unknown) => string);
}

interface MatchWithHandle {
  id: string;
  pathname: string;
  data: unknown;
  handle?: CrumbHandle;
}

export function Breadcrumbs() {
  const { t } = useTranslation("common");
  const matches = useMatches() as unknown as MatchWithHandle[];
  const crumbs = matches
    .filter((m) => Boolean(m.handle?.crumbKey ?? m.handle?.crumb))
    .map((m) => {
      let label = "";
      const key = m.handle?.crumbKey;
      if (key) {
        label = t(key);
      } else {
        const c = m.handle!.crumb;
        label = typeof c === "function" ? c(m.data) : c!;
      }
      return { pathname: m.pathname, label };
    });

  if (crumbs.length === 0) return null;

  return (
    <nav
      aria-label="Breadcrumb"
      className="flex min-w-0 items-center gap-1.5 text-[13px] text-ink-500"
    >
      {crumbs.map((crumb, idx) => (
        <Fragment key={`${crumb.pathname}-${idx}`}>
          {idx > 0 ? (
            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-ink-400" />
          ) : null}
          {idx === crumbs.length - 1 ? (
            <span className="truncate font-semibold text-ink-950">
              {crumb.label}
            </span>
          ) : (
            <Link
              to={crumb.pathname}
              className="truncate transition-colors hover:text-brand-700"
            >
              {crumb.label}
            </Link>
          )}
        </Fragment>
      ))}
    </nav>
  );
}
